import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { UiKitComponent } from "./ui-kit/ui-kit.component";
import { LoginPageComponent } from "./pages/login-page/login-page.component";
import { MyPetsPageComponent } from "./pages/my-pets-page/my-pets-page.component";
import { InvoiceListPageComponent } from "./pages/invoice-list-page/invoice-list-page.component";
import { InvoiceDetailPageComponent } from "./pages/invoice-detail-page/invoice-detail-page.component";
import { HeaderWrapperComponent } from "./pages/header-wrapper/header-wrapper.component";
import { dataResolver } from "./resolvers/data.resolver";
import { authGuard } from "./guards/auth.guard";
import { noAuthGuard } from "./guards/no-auth.guard";
import { InsuranceOverviewPageComponent } from "./pages/insurance-overview-page/insurance-overview-page.component";
import { InsuranceDetailsPageComponent } from "./pages/insurance-details-page/insurance-details-page.component";
import { ClaimsListPageComponent } from "./pages/claims-list-page/claims-list-page.component";
import { ClaimsDetailsPageComponent } from "./pages/claims-details-page/claims-details-page.component";
import { AccountComponent } from "./modules/auth/account/account.component";
import { ChangePasswordComponent } from "./modules/auth/change-password/change-password.component";
import { EditPetInfoPageComponent } from "./pages/edit-pet-info-page/edit-pet-info-page.component";
import { EditInsuranceInfoPageComponent } from "./pages/edit-insurance-info-page/edit-insurance-info-page.component";
import { editInfoCanDeactivateGuard } from "./guards/edit-info-can-deactivate.guard";
import { PetFnolPageComponent } from "./pages/pet-fnol-page/pet-fnol-page.component";
import { DesktopHomePageComponent } from "./pages/desktop-home-page/desktop-home-page.component";
import {
  DesktopSideSubcomponentComponent
} from "./modules/desktop/desktop-side-subcomponent/desktop-side-subcomponent.component";
import { FnolRequestDetailsPageComponent } from "./pages/fnol-request-details-page/fnol-request-details-page.component";
import { AccountPreviewComponent } from "./modules/auth/account-preview/account-preview.component";
import { ContactUsComponent } from "./modules/auth/contact-us/contact-us.component";
import { VouchersListComponent } from "./modules/auth/vouchers-list/vouchers-list.component";
import { VoucherDetailsComponent } from "./modules/auth/voucher-details/voucher-details.component";
import { ForgotPasswordComponent } from "./modules/auth/forgot-password/forgot-password.component";
import { ChatComponent } from "./components/chat/chat.component";
import { ChatHistoryComponent } from "./components/chat-history/chat-history.component";
import { ChatClaimDetailsComponent } from "./components/chat-claim-details/chat-claim-details.component";
import {
  CancelInsuranceReasonPageComponent
} from "./pages/cancel-insurance-reason-page/cancel-insurance-reason-page.component";
import { ChangeIbanPageComponent } from "./pages/change-iban-page/change-iban-page.component";
import { LinkCompletePageComponent } from "./pages/link-complete-page/link-complete-page.component";

const routes: Routes = [
  { path: 'auth', component: LoginPageComponent, canActivate: [noAuthGuard] },
  // No guard on purpose. The account exists but nothing is signed in yet, so a
  // guard that asks for a session would turn the customer away at the last step.
  { path: 'auth/link-complete', component: LinkCompletePageComponent },
  {
    path: 'calingo', component: HeaderWrapperComponent, resolve: { dataResolver }, canActivate: [authGuard], children: [
      {
        path: 'pet', component: MyPetsPageComponent, children: [
          { path: '', redirectTo: 'invoice', pathMatch: 'full' },
          { path: 'invoice', component: InvoiceListPageComponent },
          { path: 'invoice/:id', component: InvoiceDetailPageComponent },
          { path: 'insurance', component: InsuranceOverviewPageComponent },
          { path: 'insurance/details', component: InsuranceDetailsPageComponent },
          {
            path: 'insurance/edit-pet-info',
            component: EditPetInfoPageComponent,
            data: { hidePetCards: true },
            canDeactivate: [editInfoCanDeactivateGuard]
          },
          {
            path: 'insurance/edit-insurance-info',
            component: EditInsuranceInfoPageComponent,
            data: { hidePetCards: true },
            canDeactivate: [editInfoCanDeactivateGuard]
          },
          {
            path: 'insurance/cancel',
            component: CancelInsuranceReasonPageComponent,
            data: { hidePetCards: true },
            canDeactivate: [editInfoCanDeactivateGuard]
          },
          { path: 'claims', component: ClaimsListPageComponent },
          { path: 'claims/:id', component: ClaimsDetailsPageComponent },
          {
            path: 'fnol-details/:id',
            component: FnolRequestDetailsPageComponent
          },

          { path: 'fnol', redirectTo: '/calingo/fnol' },
          { path: 'account', redirectTo: '/calingo/account/view' },
        ]
      },
      {
        path: 'fnol',
        canActivate: [authGuard],
        component: PetFnolPageComponent,
        canDeactivate: [editInfoCanDeactivateGuard]
      },
      {
        path: 'account',
        canActivate: [authGuard],
        children: [
          {
            path: 'view',
            canActivate: [authGuard],
            component: AccountPreviewComponent,
          },
          {
            path: 'edit',
            canActivate: [authGuard],
            component: AccountComponent,
            canDeactivate: [editInfoCanDeactivateGuard]
          },
          {
            path: 'change-password', canActivate: [authGuard], component: ChangePasswordComponent
          },
          { path: 'contact-us', canActivate: [authGuard], component: ContactUsComponent },
          { path: 'vouchers', canActivate: [authGuard], component: VouchersListComponent },
          { path: 'vouchers/:id', canActivate: [authGuard], component: VoucherDetailsComponent },
          { path: 'forgot-password', canActivate: [authGuard], component: ForgotPasswordComponent },
          { path: 'change-iban', canActivate: [authGuard], component: ChangeIbanPageComponent }
        ]
      },
      {
        path: 'telemedicine', canActivate: [authGuard], children: [
          { path: 'chat', component: ChatComponent },
          { path: 'chat-history', component: ChatHistoryComponent },
          { path: 'details/:type/:id', component: ChatClaimDetailsComponent },
          { path: '', redirectTo: 'chat', pathMatch: 'full' },
        ]
      },

      { path: '**', redirectTo: 'pet' }
    ]
  },
  {
    path: 'desktop', component: HeaderWrapperComponent, resolve: { dataResolver }, canActivate: [authGuard], children: [
      {
        path: 'home', component: DesktopHomePageComponent, children: [
          {
            path: ':section', component: DesktopSideSubcomponentComponent, canDeactivate: [editInfoCanDeactivateGuard],
          },
          {
            path: ':section/:subsection',
            component: DesktopSideSubcomponentComponent,
            canDeactivate: [editInfoCanDeactivateGuard],
          },
          {
            path: ':section/:subsection/:id',
            component: DesktopSideSubcomponentComponent,
            canDeactivate: [editInfoCanDeactivateGuard],
          },
          {
            path: ':section/:subsection/:type/:id',
            component: DesktopSideSubcomponentComponent,
            canDeactivate: [editInfoCanDeactivateGuard],
          }
        ]
      },
    ]
  },
  { path: '**', redirectTo: 'auth' }
];

@NgModule({
  imports: [RouterModule.forRoot(routes, {
    onSameUrlNavigation: 'reload'
  })], exports: [RouterModule]
})
export class AppRoutingModule {
}
